import React, { useState, useRef, useCallback, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import {
    Play, RotateCcw, ChevronDown, CheckCircle2,
    XCircle, Terminal, Loader2, AlertCircle,
    Clock, Code2, ChevronRight, ChevronUp,
    BookOpen, Lightbulb, Lock,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import * as apiClient from '@/services/apiClient';

/* ─── Language config ────────────────────────────────────────────────────────── */
const LANGUAGES = [
    { key: 'javascript', label: 'JavaScript',  monacoId: 'javascript', icon: '🟨' },
    { key: 'typescript', label: 'TypeScript',  monacoId: 'typescript', icon: '🔷' },
    { key: 'python',     label: 'Python',      monacoId: 'python',     icon: '🐍' },
    { key: 'java',       label: 'Java',        monacoId: 'java',       icon: '☕' },
    { key: 'cpp',        label: 'C++',         monacoId: 'cpp',        icon: '⚡' },
    { key: 'c',          label: 'C',           monacoId: 'c',          icon: '🔵' },
    { key: 'go',         label: 'Go',          monacoId: 'go',         icon: '🐹' },
    { key: 'rust',       label: 'Rust',        monacoId: 'rust',       icon: '🦀' },
    { key: 'csharp',     label: 'C#',          monacoId: 'csharp',     icon: '💜' },
    { key: 'ruby',       label: 'Ruby',        monacoId: 'ruby',       icon: '💎' },
    { key: 'kotlin',     label: 'Kotlin',      monacoId: 'kotlin',     icon: '🟣' },
    { key: 'swift',      label: 'Swift',       monacoId: 'swift',      icon: '🧡' },
    { key: 'php',        label: 'PHP',         monacoId: 'php',        icon: '🐘' },
    { key: 'r',          label: 'R',           monacoId: 'r',          icon: '📊' },
    { key: 'bash',       label: 'Bash',        monacoId: 'shell',      icon: '💻' },
];

/* ─── Default code templates per language ───────────────────────────────────── */
const TEMPLATES = {
    javascript: `// JavaScript solution
function solution(input) {
    // Write your solution here
    return input;
}

// Read from stdin
const lines = require('fs').readFileSync('/dev/stdin', 'utf8').trim().split('\\n');
console.log(solution(lines[0]));`,

    typescript: `// TypeScript solution
function solution(input: string): string {
    // Write your solution here
    return input;
}

const lines = require('fs').readFileSync('/dev/stdin', 'utf8').trim().split('\\n');
console.log(solution(lines[0]));`,

    python: `# Python solution
import sys

def solution(data):
    # Write your solution here
    return data

lines = sys.stdin.read().strip().split('\\n')
print(solution(lines[0]))`,

    java: `import java.util.*;
import java.io.*;

public class Solution {
    public static void main(String[] args) throws Exception {
        Scanner sc = new Scanner(System.in);
        // Read your input
        // String line = sc.nextLine();

        // Write your solution here
        System.out.println("Hello, World!");
    }
}`,

    cpp: `#include <bits/stdc++.h>
using namespace std;

int main() {
    ios_base::sync_with_stdio(false);
    cin.tie(NULL);

    // Read your input
    // int n; cin >> n;

    // Write your solution here
    cout << "Hello, World!" << endl;

    return 0;
}`,

    c: `#include <stdio.h>
#include <stdlib.h>
#include <string.h>

int main() {
    // Read your input
    // int n; scanf("%d", &n);

    // Write your solution here
    printf("Hello, World!\\n");

    return 0;
}`,

    go: `package main

import (
    "bufio"
    "fmt"
    "os"
)

func main() {
    scanner := bufio.NewScanner(os.Stdin)
    // Read: scanner.Scan(); line := scanner.Text()

    // Write your solution here
    fmt.Println("Hello, World!")
}`,

    rust: `use std::io::{self, BufRead};

fn main() {
    let stdin = io::stdin();
    let mut lines = stdin.lock().lines();

    // Read: let line = lines.next().unwrap().unwrap();

    // Write your solution here
    println!("Hello, World!");
}`,

    csharp: `using System;
using System.Collections.Generic;

class Solution {
    static void Main(string[] args) {
        // Read: string line = Console.ReadLine();

        // Write your solution here
        Console.WriteLine("Hello, World!");
    }
}`,

    ruby: `# Ruby solution
lines = $stdin.read.strip.split("\\n")

# Write your solution here
puts "Hello, World!"`,

    kotlin: `fun main() {
    // Read: val line = readLine() ?: ""

    // Write your solution here
    println("Hello, World!")
}`,

    swift: `import Foundation

// Read: let line = readLine() ?? ""

// Write your solution here
print("Hello, World!")`,

    php: `<?php
// Read: $line = trim(fgets(STDIN));

// Write your solution here
echo "Hello, World!\\n";
?>`,

    r: `# Read input
lines <- readLines(con = stdin())

# Write your solution here
cat("Hello, World!\\n")`,

    bash: `#!/bin/bash
# Read: read line

# Write your solution here
echo "Hello, World!"`,
};

/* ─── Monaco editor options ─────────────────────────────────────────────────── */
const EDITOR_OPTIONS = {
    fontSize:            14,
    fontFamily:          "'JetBrains Mono', 'Fira Code', 'Cascadia Code', Consolas, monospace",
    fontLigatures:       true,
    lineNumbers:         'on',
    minimap:             { enabled: false },
    scrollBeyondLastLine: false,
    wordWrap:            'on',
    tabSize:             4,
    insertSpaces:        true,
    autoIndent:          'full',
    formatOnPaste:       false, // paste is blocked — keep false
    formatOnType:        true,
    suggestOnTriggerCharacters: true,
    quickSuggestions:    true,
    bracketPairColorization: { enabled: true },
    guides: {
        bracketPairs:    true,
        indentation:     true,
    },
    renderLineHighlight: 'line',
    smoothScrolling:     true,
    cursorBlinking:      'smooth',
    cursorSmoothCaretAnimation: 'on',
    padding:             { top: 12, bottom: 12 },
    scrollbar: {
        verticalScrollbarSize:   8,
        horizontalScrollbarSize: 8,
    },
    contextmenu:         true,
};

/* ─── Language selector dropdown ─────────────────────────────────────────────── */
function LangSelector({ value, onChange, locked }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const current = LANGUAGES.find(l => l.key === value) || LANGUAGES[0];

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => !locked && setOpen(o => !o)}
                disabled={locked}
                className={cn(
                    'flex items-center gap-1.5 rounded-md border border-[#3c3c3c] bg-[#2d2d2d]',
                    'px-2.5 py-1.5 text-[13px] font-medium text-[#cccccc] transition-colors',
                    'hover:border-[#555] hover:bg-[#383838]',
                    locked && 'cursor-not-allowed opacity-60',
                )}
            >
                <span className="text-base leading-none">{current.icon}</span>
                <span>{current.label}</span>
                <ChevronDown className={cn('h-3 w-3 transition-transform', open && 'rotate-180')} />
            </button>
            {open && (
                <div className="absolute left-0 top-full z-50 mt-1 w-44 overflow-hidden rounded-lg border border-[#3c3c3c] bg-[#252526] shadow-2xl">
                    <div className="max-h-60 overflow-y-auto py-1">
                        {LANGUAGES.map(lang => (
                            <button
                                key={lang.key}
                                onClick={() => { onChange(lang.key); setOpen(false); }}
                                className={cn(
                                    'flex w-full items-center gap-2.5 px-3 py-1.5 text-[13px] text-[#cccccc] transition-colors',
                                    'hover:bg-[#2a2d2e]',
                                    lang.key === value && 'bg-[#04395e] text-white',
                                )}
                            >
                                <span className="text-base">{lang.icon}</span>
                                <span>{lang.label}</span>
                                {lang.key === value && <CheckCircle2 className="ml-auto h-3 w-3 text-[#4fc1ff]" />}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ─── Test case result badge ─────────────────────────────────────────────────── */
function TestResult({ tc, idx, actual }) {
    const passed = actual !== undefined && actual.trim() === tc.expectedOutput.trim();
    return (
        <div className={cn(
            'rounded-lg border p-3 text-[12px] font-mono',
            passed ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5'
        )}>
            <div className="flex items-center gap-2 mb-2">
                {passed
                    ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                    : <XCircle className="h-3.5 w-3.5 text-red-500" />}
                <span className={cn('font-semibold text-[11px] uppercase tracking-wide', passed ? 'text-emerald-400' : 'text-red-400')}>
                    Test {idx + 1} — {passed ? 'Passed' : 'Failed'}
                </span>
            </div>
            <div className="space-y-1 text-[#cccccc]">
                <div><span className="text-[#858585]">Input:    </span><span className="text-[#9cdcfe]">{tc.input}</span></div>
                <div><span className="text-[#858585]">Expected: </span><span className="text-emerald-400">{tc.expectedOutput}</span></div>
                {actual !== undefined && (
                    <div><span className="text-[#858585]">Got:      </span>
                        <span className={passed ? 'text-emerald-400' : 'text-red-400'}>{actual || '(empty)'}</span>
                    </div>
                )}
            </div>
        </div>
    );
}

/* ─── Main CodingIDE component ───────────────────────────────────────────────── */
const CodingIDE = ({
    question,
    codeTemplate = '',
    language: initialLanguage = 'python',
    testCases = [],
    constraints = '',
    hints = [],
    onCodeSubmit,
}) => {
    const [language, setLanguage]       = useState(initialLanguage || 'python');
    const [code, setCode]               = useState(codeTemplate || TEMPLATES[initialLanguage || 'python'] || '');
    const [stdin, setStdin]             = useState('');
    const [output, setOutput]           = useState(null);  // { stdout, stderr, exitCode, stage }
    const [running, setRunning]         = useState(false);
    const [testResults, setTestResults] = useState([]);    // [{ actual, passed }]
    const [runMode, setRunMode]         = useState('run'); // 'run' | 'test'
    const [outputOpen, setOutputOpen]   = useState(false);
    const [activeTab, setActiveTab]     = useState('output'); // 'output' | 'tests' | 'stdin'
    const [submitConfirm, setSubmitConfirm] = useState(false);
    const [problemOpen, setProblemOpen] = useState(true);  // problem statement panel
    const [hintsOpen, setHintsOpen]     = useState(false);
    const editorRef = useRef(null);

    // Language templates cache per language (so switching languages shows template)
    const codeMap = useRef({ [initialLanguage || 'python']: codeTemplate || TEMPLATES[initialLanguage || 'python'] || '' });

    const handleLangChange = useCallback((newLang) => {
        // Save current code for current language
        codeMap.current[language] = code;
        // Load saved code for new language (or template)
        const saved = codeMap.current[newLang] || TEMPLATES[newLang] || '';
        setLanguage(newLang);
        setCode(saved);
        setOutput(null);
        setTestResults([]);
    }, [language, code]);

    const monacoLang = LANGUAGES.find(l => l.key === language)?.monacoId || 'python';

    const handleEditorMount = (editor) => {
        editorRef.current = editor;
        // Block paste via editor action interception
        editor.onDidPaste(() => {
            // We allow paste in the IDE for developer comfort — only block in textarea fallback
        });
    };

    const runCode = useCallback(async (mode = 'run') => {
        if (!code.trim()) return;
        setRunning(true);
        setOutput(null);
        setTestResults([]);
        setOutputOpen(true);
        setActiveTab(mode === 'test' && testCases.length > 0 ? 'tests' : 'output');

        try {
            if (mode === 'test' && testCases.length > 0) {
                // Run against each test case sequentially
                const results = [];
                for (const tc of testCases) {
                    try {
                        const res = await apiClient.executeCode({ language, code, stdin: tc.input || '' });
                        const actual = (res.stdout || '').trimEnd();
                        results.push({ actual, passed: actual === tc.expectedOutput?.trimEnd(), stderr: res.stderr, exitCode: res.exitCode });
                    } catch {
                        results.push({ actual: '', passed: false, stderr: 'Execution error', exitCode: -1 });
                    }
                }
                setTestResults(results);
                const passed = results.filter(r => r.passed).length;
                setOutput({
                    stdout: `${passed}/${results.length} test cases passed`,
                    stderr: '',
                    exitCode: passed === results.length ? 0 : 1,
                    stage: 'test',
                });
            } else {
                // Single run with custom stdin
                const res = await apiClient.executeCode({ language, code, stdin });
                setOutput(res);
            }
        } catch (err) {
            setOutput({ stdout: '', stderr: err.message || 'Execution failed', exitCode: -1 });
        } finally {
            setRunning(false);
        }
    }, [code, language, stdin, testCases]);

    const handleReset = () => {
        const template = codeTemplate || TEMPLATES[language] || '';
        setCode(template);
        codeMap.current[language] = template;
        setOutput(null);
        setTestResults([]);
    };

    const handleSubmit = () => {
        if (!code.trim()) return;
        if (!submitConfirm) { setSubmitConfirm(true); return; }
        setSubmitConfirm(false);
        if (onCodeSubmit) onCodeSubmit(code);
    };

    const passed = testResults.filter(r => r.passed).length;
    const hasOutput = output !== null;
    const isError = output && (output.exitCode !== 0 || output.stderr);

    return (
        <div className="flex h-full flex-col overflow-hidden rounded-xl border border-[#3c3c3c] bg-[#1e1e1e] shadow-2xl">

            {/* ── Toolbar ──────────────────────────────────────────────────────── */}
            <div className="flex items-center gap-2 border-b border-[#3c3c3c] bg-[#252526] px-3 py-2">
                <Code2 className="h-4 w-4 text-[#4fc1ff] shrink-0" />
                <span className="text-[12px] font-semibold text-[#cccccc] mr-1">Language:</span>
                <LangSelector value={language} onChange={handleLangChange} locked={false} />

                <div className="ml-auto flex items-center gap-1.5">
                    {/* Reset */}
                    <button
                        onClick={handleReset}
                        title="Reset to template"
                        className="flex items-center gap-1 rounded px-2 py-1 text-[12px] text-[#858585] hover:bg-[#2a2d2e] hover:text-[#cccccc] transition-colors"
                    >
                        <RotateCcw className="h-3 w-3" />
                        <span className="hidden sm:inline">Reset</span>
                    </button>

                    {/* Run (with stdin) */}
                    <button
                        onClick={() => runCode('run')}
                        disabled={running || !code.trim()}
                        className={cn(
                            'flex items-center gap-1.5 rounded px-3 py-1.5 text-[13px] font-semibold transition-all',
                            'bg-[#4caf50]/20 text-[#4caf50] border border-[#4caf50]/40',
                            'hover:bg-[#4caf50]/30 hover:border-[#4caf50]/60',
                            'disabled:opacity-40 disabled:cursor-not-allowed',
                        )}
                    >
                        {running && runMode === 'run' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5 fill-current" />}
                        Run
                    </button>

                    {/* Test cases (only show when testCases exist) */}
                    {testCases.length > 0 && (
                        <button
                            onClick={() => { setRunMode('test'); runCode('test'); }}
                            disabled={running || !code.trim()}
                            className={cn(
                                'flex items-center gap-1.5 rounded px-3 py-1.5 text-[13px] font-semibold transition-all',
                                'bg-[#007acc]/20 text-[#4fc1ff] border border-[#007acc]/40',
                                'hover:bg-[#007acc]/30 hover:border-[#007acc]/60',
                                'disabled:opacity-40 disabled:cursor-not-allowed',
                            )}
                        >
                            {running && runMode === 'test' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                            Test
                        </button>
                    )}

                    {/* Divider */}
                    <div className="h-5 w-px bg-[#3c3c3c] mx-1" />

                    {/* Submit */}
                    {submitConfirm ? (
                        <div className="flex items-center gap-1">
                            <span className="text-[12px] text-[#ffcc02]">Confirm?</span>
                            <button onClick={handleSubmit}
                                className="rounded px-2 py-1 text-[12px] font-semibold bg-[#f14c4c]/20 text-[#f14c4c] border border-[#f14c4c]/40 hover:bg-[#f14c4c]/30">
                                Yes, Submit
                            </button>
                            <button onClick={() => setSubmitConfirm(false)}
                                className="rounded px-2 py-1 text-[12px] text-[#858585] hover:text-[#cccccc]">
                                Cancel
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={handleSubmit}
                            disabled={!code.trim()}
                            className={cn(
                                'flex items-center gap-1.5 rounded px-3 py-1.5 text-[13px] font-semibold transition-all',
                                'bg-[#007acc] text-white',
                                'hover:bg-[#0090ff]',
                                'disabled:opacity-40 disabled:cursor-not-allowed',
                            )}
                        >
                            Submit
                            <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
            </div>

            {/* ── Problem statement panel ──────────────────────────────────────── */}
            {question && (
                <div className="border-b border-[#3c3c3c] bg-[#1a1a2e]/80">
                    {/* Problem header row — always visible */}
                    <div
                        className="flex cursor-pointer items-center gap-2 px-3 py-2 select-none hover:bg-[#2a2d2e] transition-colors"
                        onClick={() => setProblemOpen(o => !o)}
                    >
                        <BookOpen className="h-3.5 w-3.5 text-[#4fc1ff] shrink-0" />
                        <span className="text-[12px] font-semibold text-[#4fc1ff]">Problem Statement</span>
                        <ChevronDown className={cn('ml-auto h-3.5 w-3.5 text-[#858585] transition-transform', problemOpen && 'rotate-180')} />
                    </div>

                    {/* Collapsible body */}
                    {problemOpen && (
                        <div className="max-h-52 overflow-y-auto px-4 pb-3 pt-1 space-y-2.5">
                            {/* Question text */}
                            <p className="text-[13px] leading-relaxed text-[#d4d4d4] whitespace-pre-wrap">{question}</p>

                            {/* Constraints */}
                            {constraints && (
                                <div className="rounded-md border border-[#3c3c3c] bg-[#252526] px-3 py-2">
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#858585] mb-1">Constraints</p>
                                    <p className="text-[12px] text-[#cccccc] font-mono whitespace-pre-wrap">{constraints}</p>
                                </div>
                            )}

                            {/* Hints toggle */}
                            {hints && hints.length > 0 && (
                                <div>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setHintsOpen(o => !o); }}
                                        className="flex items-center gap-1.5 text-[11px] text-[#ffcc02] hover:text-[#ffe066] transition-colors"
                                    >
                                        <Lightbulb className="h-3 w-3" />
                                        {hintsOpen ? 'Hide hints' : `Show hints (${hints.length})`}
                                    </button>
                                    {hintsOpen && (
                                        <ul className="mt-1.5 space-y-1 pl-4">
                                            {hints.map((h, i) => (
                                                <li key={i} className="text-[12px] text-[#cccccc] list-disc">{h}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ── Editor area ──────────────────────────────────────────────────── */}
            <div className="flex-1 min-h-0">
                <Editor
                    height="100%"
                    language={monacoLang}
                    value={code}
                    onChange={(val) => setCode(val || '')}
                    onMount={handleEditorMount}
                    theme="vs-dark"
                    options={EDITOR_OPTIONS}
                    loading={
                        <div className="flex h-full items-center justify-center bg-[#1e1e1e]">
                            <div className="flex flex-col items-center gap-2">
                                <Loader2 className="h-6 w-6 animate-spin text-[#4fc1ff]" />
                                <span className="text-[12px] text-[#858585]">Loading editor…</span>
                            </div>
                        </div>
                    }
                />
            </div>

            {/* ── Output panel (collapsible) ────────────────────────────────────── */}
            <div className={cn(
                'border-t border-[#3c3c3c] bg-[#1e1e1e] transition-all duration-200',
                outputOpen ? 'h-52' : 'h-9',
            )}>
                {/* Panel header / toggle */}
                <div
                    className="flex h-9 cursor-pointer items-center gap-2 border-b border-[#3c3c3c] bg-[#252526] px-3 select-none"
                    onClick={() => setOutputOpen(o => !o)}
                >
                    <Terminal className="h-3.5 w-3.5 text-[#858585] shrink-0" />

                    {/* Tab pills */}
                    <div className="flex items-center gap-0.5" onClick={e => e.stopPropagation()}>
                        {['output', 'stdin', ...(testCases.length ? ['tests'] : [])].map(tab => (
                            <button
                                key={tab}
                                onClick={() => { setActiveTab(tab); setOutputOpen(true); }}
                                className={cn(
                                    'rounded px-2 py-0.5 text-[11px] font-medium capitalize transition-colors',
                                    activeTab === tab && outputOpen
                                        ? 'bg-[#37373d] text-[#cccccc]'
                                        : 'text-[#858585] hover:text-[#cccccc]',
                                )}
                            >
                                {tab === 'tests' ? `Tests (${passed}/${testCases.length})` : tab}
                            </button>
                        ))}
                    </div>

                    {/* Status indicators */}
                    {running && <Loader2 className="ml-1 h-3 w-3 animate-spin text-[#4fc1ff]" />}
                    {!running && hasOutput && !isError && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-emerald-500" />}
                    {!running && hasOutput && isError && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-red-500" />}

                    <ChevronUp className={cn('ml-auto h-3.5 w-3.5 text-[#858585] transition-transform', !outputOpen && 'rotate-180')} />
                </div>

                {/* Panel body */}
                {outputOpen && (
                    <div className="h-[calc(100%-36px)] overflow-auto p-3">
                        {/* Output tab */}
                        {activeTab === 'output' && (
                            <div className="h-full">
                                {running ? (
                                    <div className="flex items-center gap-2 text-[#858585] text-[12px]">
                                        <Loader2 className="h-4 w-4 animate-spin" /> Executing…
                                    </div>
                                ) : !hasOutput ? (
                                    <p className="text-[12px] text-[#858585] italic">Click Run to execute your code.</p>
                                ) : (
                                    <div className="space-y-2 font-mono text-[13px]">
                                        {output.stdout && (
                                            <div>
                                                <p className="text-[10px] text-[#858585] uppercase tracking-wider mb-1">stdout</p>
                                                <pre className="whitespace-pre-wrap text-[#d4d4d4] leading-relaxed">{output.stdout}</pre>
                                            </div>
                                        )}
                                        {output.stderr && (
                                            <div>
                                                <p className="text-[10px] text-red-400 uppercase tracking-wider mb-1">stderr</p>
                                                <pre className="whitespace-pre-wrap text-red-400 leading-relaxed">{output.stderr}</pre>
                                            </div>
                                        )}
                                        {!output.stdout && !output.stderr && (
                                            <p className="text-[#858585] italic">No output.</p>
                                        )}
                                        <div className="flex items-center gap-2 pt-1 border-t border-[#3c3c3c] mt-2">
                                            <span className="text-[10px] text-[#858585]">Exit code:</span>
                                            <span className={cn(
                                                'text-[10px] font-mono font-bold',
                                                output.exitCode === 0 ? 'text-emerald-400' : 'text-red-400'
                                            )}>
                                                {output.exitCode ?? 0}
                                            </span>
                                            {output.signal && <span className="text-[10px] text-amber-400">({output.signal})</span>}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Stdin tab */}
                        {activeTab === 'stdin' && (
                            <div className="h-full flex flex-col gap-2">
                                <p className="text-[11px] text-[#858585]">Custom stdin (passed to your program on Run):</p>
                                <textarea
                                    value={stdin}
                                    onChange={e => setStdin(e.target.value)}
                                    placeholder="Enter your test input here…"
                                    className="flex-1 resize-none rounded border border-[#3c3c3c] bg-[#1e1e1e] p-2 font-mono text-[13px] text-[#d4d4d4] placeholder:text-[#555] outline-none focus:border-[#007acc]"
                                    spellCheck={false}
                                />
                            </div>
                        )}

                        {/* Test cases tab */}
                        {activeTab === 'tests' && (
                            <div className="space-y-2">
                                {testCases.length === 0 ? (
                                    <p className="text-[12px] text-[#858585] italic">No test cases defined for this question.</p>
                                ) : testResults.length === 0 ? (
                                    <div>
                                        <p className="text-[12px] text-[#858585] mb-3">Click <strong className="text-[#4fc1ff]">Test</strong> to run against all test cases.</p>
                                        <div className="space-y-2">
                                            {testCases.map((tc, i) => (
                                                <div key={i} className="rounded-lg border border-[#3c3c3c] p-3 text-[12px] font-mono text-[#cccccc]">
                                                    <p className="text-[10px] text-[#858585] mb-1">Test {i + 1}</p>
                                                    <div><span className="text-[#858585]">Input:    </span><span className="text-[#9cdcfe]">{tc.input}</span></div>
                                                    <div><span className="text-[#858585]">Expected: </span><span className="text-emerald-400">{tc.expectedOutput}</span></div>
                                                    {tc.description && <p className="text-[#858585] mt-1">{tc.description}</p>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <p className="text-[11px] text-[#858585] mb-2">{passed}/{testCases.length} passed</p>
                                        {testCases.map((tc, i) => (
                                            <TestResult key={i} tc={tc} idx={i} actual={testResults[i]?.actual} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ── Status bar (VS Code style) ────────────────────────────────────── */}
            <div className="flex items-center gap-4 border-t border-[#3c3c3c] bg-[#007acc] px-3 py-0.5 text-[11px] text-white/90 select-none">
                <span>{LANGUAGES.find(l => l.key === language)?.label || language}</span>
                <span className="ml-auto flex items-center gap-1">
                    <Clock className="h-2.5 w-2.5" />
                    <span className="text-white/70">Judge0 CE</span>
                </span>
                {testCases.length > 0 && testResults.length > 0 && (
                    <span className={cn(passed === testCases.length ? 'text-emerald-300' : 'text-red-300')}>
                        Tests: {passed}/{testCases.length}
                    </span>
                )}
            </div>
        </div>
    );
};

export default CodingIDE;
